import React, { useEffect, useState } from "react";
import { FaSave, FaTimes } from "react-icons/fa";
import Button from "../../../components/Button/Button";
import { toast } from "react-toastify";
import { updateHighLevelSession } from "../../../services/highLevelSessionServices";

export default function EditHighLevelAdherentSessionModal({
  isOpen,
  onClose,
  afterSave,
  session,
  adherent,
  categories = [],
}) {
  const [form, setForm] = useState({
    adherent_id: adherent?.id || session?.adherent_id || "",
    seance_date: session?.seance_date || "",
    categorie_seance: session?.categorie_seance || categories[0] || "Entrainement Judo club",
    nom_seance: session?.nom_seance || "",
    lieu_seance: session?.lieu_seance || "",
    duree_minutes: session?.duree_minutes ?? 60,
    qualite_note: session?.qualite_note ?? "",
    observations_seance: session?.observations_seance || "",
    is_done: !!session?.is_done,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen || !session) return;
    setForm({
      adherent_id: adherent?.id || session?.adherent_id || "",
      seance_date: session?.seance_date || "",
      categorie_seance: session?.categorie_seance || categories[0] || "Entrainement Judo club",
      nom_seance: session?.nom_seance || "",
      lieu_seance: session?.lieu_seance || "",
      duree_minutes: session?.duree_minutes ?? 60,
      qualite_note: session?.qualite_note ?? "",
      observations_seance: session?.observations_seance || "",
      is_done: !!session?.is_done,
    });
    setSubmitting(false);
  }, [isOpen, session, adherent, categories]);

  if (!isOpen || !session) return null;

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const canSubmit = form.adherent_id && form.seance_date && form.categorie_seance;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        adherent_id: Number(form.adherent_id),
        duree_minutes: Number(form.duree_minutes || 0),
        qualite_note: form.qualite_note ? Number(form.qualite_note) : null,
      };
      await updateHighLevelSession(session.id, payload);
      toast.success("Séance mise à jour");
      afterSave?.();
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} aria-hidden />

      {/* Modal */}
      <div className="relative w-full max-w-3xl max-h-[80vh] overflow-y-auto rounded-2xl shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
          <h3 className="text-white text-lg sm:text-xl font-bold text-center uppercase">
            Modifier la séance — {adherent?.prenom} {adherent?.nom}
          </h3>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Date</label>
              <input
                type="date"
                name="seance_date"
                value={form.seance_date}
                onChange={onChange}
                className="w-full rounded-xl border px-3 py-2"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Catégorie</label>
              <select
                name="categorie_seance"
                value={form.categorie_seance}
                onChange={onChange}
                className="w-full rounded-xl border px-3 py-2"
                required
              >
                {(categories.length ? categories : [
                  "Entrainement Judo club",
                  "Entrainement Structure",
                  "Technique club",
                  "Technique structure",
                  "Musculation",
                  "Course ",
                  "Prépa associé ou dissocié",
                  "Stage ou Training camp",
                  "Competition",
                ]).map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Nom de séance</label>
              <input
                type="text"
                name="nom_seance"
                value={form.nom_seance}
                onChange={onChange}
                className="w-full rounded-xl border px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Lieu</label>
              <input
                type="text"
                name="lieu_seance"
                value={form.lieu_seance}
                onChange={onChange}
                className="w-full rounded-xl border px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Durée (minutes)</label>
              <input
                type="number"
                min={0}
                max={1440}
                name="duree_minutes"
                value={form.duree_minutes}
                onChange={onChange}
                className="w-full rounded-xl border px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Qualité (1-5)</label>
              <input
                type="number"
                min={1}
                max={5}
                name="qualite_note"
                value={form.qualite_note}
                onChange={onChange}
                className="w-full rounded-xl border px-3 py-2"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium mb-1">Observations</label>
              <textarea
                name="observations_seance"
                value={form.observations_seance}
                onChange={onChange}
                rows={3}
                className="w-full rounded-xl border px-3 py-2"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" name="is_done" checked={!!form.is_done} onChange={onChange} />
                <span>Séance terminée</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center px-3 py-2 rounded-xl border"
            >
              <FaTimes className="mr-2" /> Annuler
            </button>
            <Button
              text={submitting ? "Enregistrement…" : "Enregistrer"}
              icon={FaSave}
              type="submit"
              className={`!rounded-xl ${!canSubmit ? "opacity-60 pointer-events-none" : ""}`}
              disabled={!canSubmit}
            />
          </div>
        </form>
      </div>
    </div>
  );
}
